/**
 * Sessão do usuário no frontend.
 *
 * Como o token é um cookie httpOnly, o React não tem como saber se há sessão
 * olhando o navegador. A única fonte de verdade é o servidor: perguntamos a ele,
 * uma vez, em `GET /auth/eu`. É por isso que existe o estado `carregando` — sem
 * ele, o Guard mandaria todo mundo para o login no primeiro render.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type Usuario } from '@/lib/api';

type Contexto = {
  usuario: Usuario | null;
  carregando: boolean;
  recarregar: () => Promise<void>;
  sair: () => Promise<void>;
};

const SessaoContexto = createContext<Contexto | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);

  const recarregar = useCallback(async () => {
    const r = await api.get<Usuario>('/auth/eu');
    setUsuario(r.ok ? r.dados : null);
    setCarregando(false);
  }, []);

  const sair = useCallback(async () => {
    await api.post('/auth/logout');
    setUsuario(null);
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  return (
    <SessaoContexto.Provider value={{ usuario, carregando, recarregar, sair }}>
      {children}
    </SessaoContexto.Provider>
  );
}

export function useSessao() {
  const ctx = useContext(SessaoContexto);
  if (!ctx) throw new Error('useSessao precisa estar dentro de <ProvedorSessao>');
  return ctx;
}
